import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import CodesTab from '@components/interview/analysis-components/CodesTab';
import TranscriptsTab from '@components/interview/analysis-components/TranscriptsTab';
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { CORE_LENSES } from '@constants/analysisLenses';
import { useMixpanel } from '@context/MixpanelProvider';
import { useFetchNextAnalysis, useSingleInterview } from '@hooks/getQueries';
import { useContinuePausedAnalysis } from '@hooks/postQueries';
import usePermission from '@hooks/usePermission';
import CodeIcon from '@mui/icons-material/Code';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import InterviewCodebook from '@pages/Interview/details/codebook';
import InterviewOverallAnalysis from '@pages/Interview/details/overall-analysis';
import InterviewThemes from '@pages/Interview/details/themes';
import { ANALYSIS_GROUNDED_EMERGENCE_EVENTS } from '@utils/eventSchema';
import { useState } from 'react';
import { useParams } from 'react-router';

// Check if grounded emergence is an enterprise feature (disabled for regular users)
const groundedEmergenceLens = CORE_LENSES.find((lens) => lens.id === 'grounded_emergence');
const isEnterpriseFeature = groundedEmergenceLens?.enterprise === true;

const GroundedEmergenceAnalysis = ({ item }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { id } = useParams();
  const { mutate: continueAnalysis, isPending: isContinuing } = useContinuePausedAnalysis(id);
  const { data: interview } = useSingleInterview(id);
  const { data: nextAnalysis } = useFetchNextAnalysis(id);
  const { trackEvent } = useMixpanel();

  const { data: permissions } = usePermission({ study: interview });

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    // Index-aligned with the Tab JSX below: 0=Transcripts, 1=Codes,
    // 2=Codebook, 3=Themes, 4=Report.
    const tabNames = ['transcripts', 'codes', 'codebook', 'themes', 'report'];
    trackEvent('analysis_grounded_emergence', {
      ...ANALYSIS_GROUNDED_EMERGENCE_EVENTS.GROUNDED_EMERGENCE_TAB_CLICKED,
      tab: tabNames[newValue],
    });
  };

  if (!item) {
    return <Typography>Loading analysis details...</Typography>;
  }

  const { analysis_item_id, analysis_lens_type, created_at, status } = item;
  const lensName = analysis_lens_type || 'grounded_emergence';

  // Power-user pause: when the worker stopped after the codebook step the
  // analysis_item lands in `paused_for_review`. The Codebook / Codes /
  // Transcripts tabs already exist as read-only views — the banner below
  // promotes them to edit-mode and surfaces the Continue button.
  const isPausedForReview = status === 'paused_for_review';
  const handleContinue = () => {
    if (!analysis_item_id || isContinuing) return;
    continueAnalysis({ analysisItemId: analysis_item_id });
  };

  return (
    <>
      <Card variant="outlined" sx={{ p: 0 }}>
        {/* Hide warning message about changes detected when feature is enterprise-only */}
        {!isEnterpriseFeature && (
          <Box p={1.5}>
            {nextAnalysis?.message && <Alert severity="warning">{nextAnalysis?.message}</Alert>}
          </Box>
        )}
        {/* Show enterprise notice when feature is disabled */}
        {isEnterpriseFeature && (
          <Box p={1.5}>
            <Alert severity="info" icon={<LockOutlinedIcon />}>
              Thematic Analysis requires additional workspace access. Contact service@qualz.ai to
              enable this feature.
            </Alert>
          </Box>
        )}
        <CardHeader
          title={
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
            >
              Grounded Theory Emergence
              <AnalysisLensHelperText lensName={lensName} level="run" size="small" sx={{ ml: 1 }} />
            </Typography>
          }
          subheader={`Analysis Date: ${new Date(created_at).toLocaleDateString()} | ID: ${analysis_item_id}`}
          avatar={<CodeIcon color="primary" />}
        />
        <Divider />
        {isPausedForReview ? (
          <Alert
            severity="info"
            icon={<RateReviewOutlinedIcon />}
            sx={{ borderRadius: 0, alignItems: 'center' }}
            action={
              <Button
                color="primary"
                variant="contained"
                onClick={handleContinue}
                disabled={isContinuing}
                startIcon={<PlayArrowRoundedIcon />}
                sx={{ minWidth: 200 }}
              >
                {isContinuing ? 'Resuming…' : 'Continue analysis'}
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Review codes before themes are generated
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Open the <strong>Transcripts &amp; Documents</strong> tab to review the codes the AI
              attached to each passage. Hover any code chip to <strong>rename</strong>,{' '}
              <strong>unassign</strong>, or <strong>delete</strong> it, or click{' '}
              <strong>+ Add code</strong> under a participant turn to attach an existing code or
              create a new one. When you&apos;re done, click <strong>Continue Analysis</strong> to
              fold your edits in and generate the codebook, themes, and report.
            </Typography>
          </Alert>
        ) : null}
        <CardContent sx={{ p: 0 }}>
          {/* Tab Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => handleTabChange(newValue)}
              aria-label="Grounded Analysis Tabs"
            >
              <Tab
                label="Transcripts & Documents"
                id="grounded-tab-0"
                aria-controls="grounded-tabpanel-0"
              />
              <Tab label="Codes" id="grounded-tab-1" aria-controls="grounded-tabpanel-1" />
              <Tab label="Codebook" id="grounded-tab-2" aria-controls="grounded-tabpanel-2" />
              <Tab label="Themes" id="grounded-tab-3" aria-controls="grounded-tabpanel-3" />
              <Tab label="Report" id="grounded-tab-4" aria-controls="grounded-tabpanel-4" />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <Box>
            <div
              role="tabpanel"
              hidden={activeTab !== 0}
              id="grounded-tabpanel-0"
              aria-labelledby="grounded-tab-0"
            >
              {activeTab === 0 && (
                <TranscriptsTab item={item} isPausedForReview={isPausedForReview} />
              )}
            </div>
            <div
              role="tabpanel"
              hidden={activeTab !== 1}
              id="grounded-tabpanel-1"
              aria-labelledby="grounded-tab-1"
            >
              {activeTab === 1 && (
                <CodesTab item={item} canEdit={permissions?.studyPermission?.edit || false} />
              )}
            </div>
            <div
              role="tabpanel"
              hidden={activeTab !== 2}
              id="grounded-tabpanel-2"
              aria-labelledby="grounded-tab-2"
            >
              {activeTab === 2 && (
                <InterviewCodebook
                  canEdit={permissions?.studyPermission?.edit || false}
                  analysisItemId={analysis_item_id}
                />
              )}
            </div>
            <div
              role="tabpanel"
              hidden={activeTab !== 3}
              id="grounded-tabpanel-3"
              aria-labelledby="grounded-tab-3"
            >
              {activeTab === 3 && (
                <InterviewThemes
                  canEdit={permissions?.studyPermission?.edit || false}
                  analysisItemId={analysis_item_id}
                />
              )}
            </div>
            <div
              role="tabpanel"
              hidden={activeTab !== 4}
              id="grounded-tabpanel-4"
              aria-labelledby="grounded-tab-4"
            >
              {activeTab === 4 && (
                <InterviewOverallAnalysis
                  item={item}
                  canEdit={permissions?.studyPermission?.edit || false}
                  analysisItemId={analysis_item_id}
                />
              )}
            </div>
          </Box>
        </CardContent>
      </Card>
      <IncludedTranscripts item={item} />
    </>
  );
};

export default GroundedEmergenceAnalysis;
